import { Trash, Edit2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import Feature1 from "@stackje/shared-components/cards/feature/Feature1";
import Feature2 from "@stackje/shared-components/cards/feature/Feature2";
import { Button } from "@stackje/shared-components/ui/button";
import { Dialog, DialogTrigger } from "@stackje/shared-components/ui/dialog";
import { ClearableInput } from "@stackje/shared-components/ui/clearable-fields";
import { SimpleMarkdownEditor } from "@stackje/shared-components/ui/simple-markdown-editor";
import { Label } from "@stackje/shared-components/ui/label";
import { useColorSchemeShuffler } from "../../../hooks/useColorSchemeShuffler";
import type { CommonColor, Feature1Card } from "@stackje/common/config";
import { sampleData } from "@/components/add-card/constants";
import { FeatureEditFormDialogContent } from "./feature-edit-form-dialog-content";

// Zod schema for validation
const featureCardFormSchema = z.object({
  title: z.string().optional(),
  subtitle: z.string().optional(),
  features: z
    .array(
      z.object({
        title: z.string().min(1, "Feature title is required"),
        description: z.string().min(1, "Feature description is required"),
        image: z.string().optional(),
      })
    )
    .min(1, "At least one feature is required"),
  type: z.enum(["feature1", "feature2"]),
  colors: z.object({
    background: z.string(),
    foreground: z.string(),
    cardBackground: z.string(),
    cardForeground: z.string(),
    buttonBackground: z.string(),
    buttonForeground: z.string(),
  }),
});

export type FeatureCardFormData = z.infer<typeof featureCardFormSchema>;

export interface FeatureCardData {
  title: string;
  subtitle: string;
  features: {
    title: string;
    description: string;
    image?: string;
  }[];
  type: "feature1" | "feature2";
  colors: CommonColor;
}

interface FeatureCardFormProps {
  initialData: FeatureCardFormData;
  mode: "add" | "edit";
  onTypeChange?: (type: "feature1" | "feature2") => void;
}

export function useFeatureCardForm({
  initialData,
  mode,
  onTypeChange,
}: FeatureCardFormProps) {
  const form = useForm<FeatureCardFormData>({
    resolver: zodResolver(featureCardFormSchema),
    defaultValues: {
      ...initialData,
      features: initialData.features || [],
    },
  });

  const { fields, append, remove, update } = useFieldArray({
    control: form.control,
    name: "features",
  });

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingFeatureIndex, setEditingFeatureIndex] = useState<number | null>(
    null
  );
  const [editingFeatureData, setEditingFeatureData] = useState<
    | {
        title: string;
        description: string;
        image?: { src: string; alt?: string };
      }
    | undefined
  >(undefined);

  const { colors, setColors, shuffleColors } = useColorSchemeShuffler(
    initialData.colors
  );

  // Watch form values for preview updates
  const watchedValues = form.watch();
  const title = watchedValues.title || "";
  const subtitle = watchedValues.subtitle || "";
  const features = watchedValues.features || [];
  const type = watchedValues.type;
  const isDirty = form.formState.isDirty;

  useEffect(() => {
    setColors(initialData.colors);
    form.setValue("colors", initialData.colors);
  }, [initialData.colors, setColors, form]);

  // Update form colors when shuffled
  useEffect(() => {
    form.setValue("colors", colors);
  }, [colors, form]);

  const toggleType = (newType?: string) => {
    if (newType) {
      form.setValue("type", newType as "feature1" | "feature2");
      onTypeChange?.(newType as "feature1" | "feature2");
      return;
    }

    // Toggle between feature1 and feature2
    const types = ["feature1", "feature2"] as const;
    const currentIndex = types.indexOf(type as "feature1" | "feature2");
    const nextIndex = (currentIndex + 1) % types.length;
    const nextType = types[nextIndex];
    form.setValue("type", nextType);
    onTypeChange?.(nextType);
  };

  const handleOpenChangeDialog = (open: boolean) => {
    setDialogOpen(open);
    if (!open) {
      setEditingFeatureIndex(null);
      setEditingFeatureData(undefined);
    }
  };

  const handleFeatureSubmit = (data: {
    title: string;
    description: string;
    image?: { src: string; alt?: string };
  }) => {
    if (editingFeatureIndex !== null) {
      // Editing existing feature
      update(editingFeatureIndex, {
        title: data.title,
        description: data.description,
        image: data.image?.src || "",
      });
    } else {
      // Adding new feature
      append({
        title: data.title,
        description: data.description,
        image: data.image?.src || "",
      });
    }
    setDialogOpen(false);
  };

  const formContent = (
    <>
      <div className="space-y-2 mt-2">
        <div className="py-2">
          <h2 className="text-lg font-semibold">Feature Card</h2>
          <p className="text-sm text-muted-foreground">
            Highlight key features with images and descriptions.
          </p>
          <p className="text-sm text-muted-foreground">
            <span className="text-red-500">*</span> indicates required fields
          </p>
        </div>

        <div className="border-t pb-2 pt-4">
          <Label htmlFor="title">Main Title</Label>
          <Controller
            name="title"
            control={form.control}
            render={({ field }) => (
              <ClearableInput
                id="title"
                value={field.value || ""}
                onChange={field.onChange}
                placeholder="Key Features"
              />
            )}
          />
        </div>

        <div className="py-2">
          <Label htmlFor="subtitle">Subtitle</Label>
          <Controller
            name="subtitle"
            control={form.control}
            render={({ field }) => (
              <SimpleMarkdownEditor
                id="subtitle"
                value={field.value || ""}
                onChange={field.onChange}
                placeholder="Discover what makes our product special"
              />
            )}
          />
        </div>

        <div className="border-t py-4">
          <h3 className="mb-4 font-medium">Features <span className="text-red-500">*</span></h3>
          {fields.map((field, index) => (
            <div
              key={field.id}
              className="flex items-center gap-4 p-4 border rounded-lg mb-3 bg-muted/30"
            >
              {/* Image preview on the left */}
              <div className="flex-shrink-0">
                {field.image ? (
                  <img
                    src={field.image}
                    alt={field.title || "Feature image"}
                    className="w-16 h-16 object-cover rounded border bg-muted"
                  />
                ) : (
                  <div className="w-16 h-16 flex items-center justify-center border border-dashed border-muted-foreground/25 rounded bg-muted text-xs text-muted-foreground">
                    No image
                  </div>
                )}
              </div>

              <div className="flex-1 min-w-0">
                <h4 className="font-medium truncate">
                  {field.title || `Feature ${index + 1}`}
                </h4>
                <p className="text-sm text-muted-foreground truncate">
                  {field.description || "No description"}
                </p>
              </div>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setEditingFeatureIndex(index);
                    setEditingFeatureData({
                      title: field.title,
                      description: field.description,
                      image: field.image ? { src: field.image } : undefined,
                    });
                    setDialogOpen(true);
                  }}
                  aria-label="Edit feature"
                >
                  <Edit2 className="h-4 w-4" />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => remove(index)}
                  aria-label="Delete feature"
                >
                  <Trash className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}

          <div className="">
            <Dialog open={dialogOpen} onOpenChange={handleOpenChangeDialog}>
              <DialogTrigger asChild>
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => {
                    setEditingFeatureIndex(null);
                    setEditingFeatureData(undefined);
                  }}
                >
                  Add Another Feature
                </Button>
              </DialogTrigger>
              <FeatureEditFormDialogContent
                key={`feature-dialog-${editingFeatureIndex ?? 'new'}`}
                isEditing={editingFeatureIndex !== null}
                initialData={editingFeatureData}
                onSubmit={handleFeatureSubmit}
                editingFeatureIndex={editingFeatureIndex}
                onClose={() => setDialogOpen(false)}
              />
            </Dialog>
          </div>
        </div>

        {/* Hidden inputs for form submission */}
        <input type="hidden" name="type" value={type} />
        <input type="hidden" name="colors" value={JSON.stringify(colors)} />

        {/* Features as individual fields for parsing */}
        {features.map((feature, index) => (
          <div key={index}>
            <input
              type="hidden"
              name={`featureTitle${index}`}
              value={feature.title || ""}
            />
            <input
              type="hidden"
              name={`featureDescription${index}`}
              value={feature.description || ""}
            />
            <input
              type="hidden"
              name={`featureImage${index}`}
              value={feature.image || ""}
            />
          </div>
        ))}
      </div>
    </>
  );

  const hasFeatures = features.length > 0;
  const hasContent = title || subtitle || hasFeatures;

  const previewContent =
    hasContent || isDirty || mode === "edit"
      ? (() => {
          const commonProps = {
            title,
            subtitle,
            features,
            colors: colors as Feature1Card["colors"],
          };

          if (type === "feature2") {
            return <Feature2 {...commonProps} />;
          }
          return <Feature1 {...commonProps} />;
        })()
      : (() => {
          const commonProps = {
            title: sampleData.features.title,
            subtitle: sampleData.features.subtitle,
            features: sampleData.features.features,
            colors: colors as Feature1Card["colors"],
          };

          if (type === "feature2") {
            return <Feature2 {...commonProps} />;
          }
          return <Feature1 {...commonProps} />;
        })();

  return {
    form,
    title,
    subtitle,
    features,
    type,
    toggleType,
    shuffleColors,
    isDirty,
    formContent,
    previewContent,
    handleSubmit: form.handleSubmit,
  };
}
